import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Compass, Grid3X3, Music, Map, Calendar, Hash, Eye,
  Flame, Award, TrendingUp, BookOpen, ExternalLink,
  ChevronRight, Loader2, Star, Zap, Trophy, Clock,
  MapPin, Tag, ChevronLeft, Sun, Moon, ShoppingBag, Heart, Target, Sparkles, Activity, Play
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  compass: Compass, grid: Grid3X3, music: Music, map: Map,
  calendar: Calendar, hash: Hash, eye: Eye,
};

const StudentPortal = () => {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const isPreview = !!studentId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('portal_theme') || 'dark');
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('portal_theme', next);
  };
  const isDark = theme === 'dark';
  const t = {
    bg: isDark ? 'bg-[#0a0a0a]' : 'bg-[#f5f3ef]',
    card: isDark ? 'bg-[#141414] border-[#252525]' : 'bg-white border-gray-200 shadow-sm',
    cardInner: isDark ? 'bg-[#1a1a1a]' : 'bg-gray-50',
    border: isDark ? 'border-[#252525]' : 'border-gray-200',
    text: isDark ? 'text-white' : 'text-gray-900',
    textSub: isDark ? 'text-gray-400' : 'text-gray-600',
    textMuted: isDark ? 'text-gray-500' : 'text-gray-400',
    textFaint: isDark ? 'text-gray-600' : 'text-gray-300',
    amber: isDark ? 'text-amber-400' : 'text-amber-600',
    amberLight: isDark ? 'text-amber-200' : 'text-amber-700',
    calEmpty: isDark ? 'bg-[#1a1a1a]' : 'bg-gray-100',
  };

  const [scheduleItems, setScheduleItems] = useState([]);
  const [scheduleView, setScheduleView] = useState('monthly');
  const [expandedSchedule, setExpandedSchedule] = useState(null);
  const [qimenItems, setQimenItems] = useState([]);
  const [merchItems, setMerchItems] = useState([]);
  const [dailyPractice, setDailyPractice] = useState(null);
  const [savingCheckin, setSavingCheckin] = useState(false);
  const [journey, setJourney] = useState(null);
  const [pointsToast, setPointsToast] = useState(null);
  const [welcomeToast, setWelcomeToast] = useState(null);
  const [tongShu, setTongShu] = useState(null);
  const [trainingData, setTrainingData] = useState(null);
  const [activeTrainingCat, setActiveTrainingCat] = useState('special');
  const [portalConfig, setPortalConfig] = useState({});
  const [lifetuneTracks, setLifetuneTracks] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const url = isPreview
        ? `${BACKEND_URL}/api/admin/portal/preview/${studentId}`
        : `${BACKEND_URL}/api/portal/dashboard`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isPreview, studentId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => {
    if (data && data.login.count > 1 && !isPreview) {
      setWelcomeToast(data.user.name);
      setTimeout(() => setWelcomeToast(null), 4000);
    }
  }, [data, isPreview]);

  const fetchSchedule = useCallback(async () => {
    try {
      const [year, month] = selectedMonth.split('-');
      const typeParam = scheduleView === 'monthly' ? `&type=monthly&month=${month}` : '';
      const res = await fetch(`${BACKEND_URL}/api/schedule?year=${year}${typeParam}`, { credentials: 'include' });
      if (res.ok) setScheduleItems(await res.json());
    } catch (e) { console.error(e); }
  }, [selectedMonth, scheduleView]);

  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  useEffect(() => {
    const fetchQimen = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/qimen`);
        if (res.ok) setQimenItems(await res.json());
      } catch (e) { console.error(e); }
    };
    const fetchMerch = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/merchandise`);
        if (res.ok) setMerchItems(await res.json());
      } catch (e) { console.error(e); }
    };
    const fetchDailyPractice = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/daily-practice`, { credentials: 'include' });
        if (res.ok) setDailyPractice(await res.json());
      } catch (e) { console.error(e); }
    };
    fetchQimen();
    fetchMerch();
    fetchDailyPractice();
    fetchTongShu();
    fetchPortalConfig();
    fetchTraining();
    fetchLifetunes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchJourney = useCallback(async () => {
    try {
      const url = isPreview
        ? `${BACKEND_URL}/api/admin/students/${studentId}/activity`
        : `${BACKEND_URL}/api/activity/me`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setJourney(await res.json());
    } catch (e) { console.error(e); }
  }, [isPreview, studentId]);

  useEffect(() => { fetchJourney(); }, [fetchJourney]);

  const fetchTongShu = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/tongshu/today`);
      if (res.ok) setTongShu(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchTraining = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/training-videos`);
      if (res.ok) setTrainingData(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchPortalConfig = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/portal-config`);
      if (res.ok) {
        const d = await res.json();
        const map = {};
        d.sections.forEach(s => { map[s.key] = s.enabled; });
        setPortalConfig(map);
      }
    } catch (e) { console.error(e); }
  }, []);

  const fetchLifetunes = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/lifetunes/tracks`);
      if (res.ok) {
        const d = await res.json();
        setLifetuneTracks(d.tracks || []);
      }
    } catch (e) { console.error(e); }
  }, []);


  const saveCheckin = async (field, value) => {
    setSavingCheckin(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/daily-practice`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) {
        const result = await res.json();
        setDailyPractice(prev => prev ? {
          ...prev,
          checkin: result.checkin,
          checkin_count_year: prev.checkin_count_year + (prev.checkin ? 0 : 1),
        } : prev);
        // Update points in the main data
        if (result.total_points !== undefined) {
          setData(prev => prev ? { ...prev, points: { ...prev.points, total: result.total_points } } : prev);
        }
        // Show points popup
        if (result.points_earned > 0) {
          setPointsToast({ points: result.points_earned, total: result.total_points });
          setTimeout(() => setPointsToast(null), 3000);
        }
        // Refresh journey
        fetchJourney();
      }
    } catch (e) { console.error(e); }
    finally { setSavingCheckin(false); }
  };

  const sectionVisible = (key) => {
    if (portalConfig[key] === false) return false;
    if (data?.user?.hidden_sections?.includes(key)) return false;
    return true;
  };

  const trackVisit = async (siteKey, url) => {
    try {
      await fetch(`${BACKEND_URL}/api/portal/ecosystem-visit`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_key: siteKey })
      });
    } catch (e) { /* silent */ }
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const { user, points, login, courses, points_log, ecosystem, ranks } = data;
  const progressToNext = points.next_rank
    ? Math.round(((points.total - points.rank.min_points) / (points.next_rank.min_points - points.rank.min_points)) * 100)
    : 100;

  return (
    <div className={`min-h-screen pt-20 transition-colors duration-300 ${isDark ? 'bg-[#0a0a0a] portal-dark' : 'bg-[#f5f3ef] portal-light'}`}>
      <style>{`
        .portal-light { color-scheme: light; }
        .portal-light .portal-text { color: #111827 !important; }
        .portal-light .portal-text-sub { color: #4b5563 !important; }
        .portal-light .portal-text-muted { color: #6b7280 !important; }
        .portal-light .portal-card { background: #fff !important; border-color: #e5e7eb !important; box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important; }
        .portal-light .portal-card-inner { background: #f3f4f6 !important; }
        .portal-light [class*="bg-[#141414]"] { background: #fff !important; }
        .portal-light [class*="bg-[#0a0a0a]"] { background: #f5f3ef !important; }
        .portal-light [class*="bg-[#1a1a1a]"] { background: #f3f4f6 !important; }
        .portal-light [class*="bg-[#1e1e1e]"] { background: #e5e7eb !important; }
        .portal-light [class*="bg-[#252525]"] { background: #e5e7eb !important; }
        .portal-light [class*="bg-[#333]"] { background: #d1d5db !important; }
        .portal-light [class*="bg-gray-800"] { background: #f3f4f6 !important; }
        .portal-light [class*="border-[#252525]"] { border-color: #e5e7eb !important; }
        .portal-light [class*="border-[#1a1a1a]"] { border-color: #e5e7eb !important; }
        .portal-light [class*="border-[#1e1e1e]"] { border-color: #e5e7eb !important; }
        .portal-light [class*="border-gray-700"] { border-color: #d1d5db !important; }
        .portal-light p, .portal-light span, .portal-light h1, .portal-light h2, .portal-light h3, .portal-light h4, .portal-light a, .portal-light label, .portal-light div {
          color: inherit;
        }
        .portal-light .text-white { color: #111827 !important; }
        .portal-light .text-gray-300 { color: #1f2937 !important; }
        .portal-light .text-gray-400 { color: #374151 !important; }
        .portal-light .text-gray-500 { color: #4b5563 !important; }
        .portal-light .text-gray-600 { color: #6b7280 !important; }
        .portal-light .text-sm.text-gray-400 { color: #4b5563 !important; }
        .portal-light .text-xs.text-gray-500 { color: #6b7280 !important; }
        .portal-light .text-\[10px\] { color: #6b7280 !important; }
        .portal-light .text-\[11px\] { color: #6b7280 !important; }
        .portal-light .text-\[9px\] { color: #9ca3af !important; }
        .portal-light .text-amber-400 { color: #b45309 !important; }
        .portal-light .text-amber-200 { color: #92400e !important; }
        .portal-light .text-amber-500\/70 { color: #b45309 !important; }
        .portal-light .text-orange-400 { color: #c2410c !important; }
        .portal-light .text-sky-300 { color: #0369a1 !important; }
        .portal-light .text-sky-400 { color: #0284c7 !important; }
        .portal-light .text-blue-400 { color: #2563eb !important; }
        .portal-light [class*="hover:text-white"]:hover { color: #111827 !important; }
        .portal-light [class*="hover:bg-gray-800"]:hover { background: #e5e7eb !important; }
        .portal-light [class*="hover:bg-[#252525]"]:hover { background: #e5e7eb !important; }
        .portal-light [class*="hover:border-amber"] { border-color: #d97706 !important; }
        .portal-light [class*="hover:border-gray-700"]:hover { border-color: #d1d5db !important; }
        .portal-dark .portal-text { color: #fff !important; }
        .portal-dark .portal-text-sub { color: #9ca3af !important; }
        .portal-dark .portal-text-muted { color: #6b7280 !important; }
        .portal-dark .portal-card { background: #141414 !important; border-color: #252525 !important; }
        .portal-dark .portal-card-inner { background: #1a1a1a !important; }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ─── Welcome Back Popup ─── */}
        {welcomeToast && (
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-testid="welcome-toast">
            <div className={`px-8 py-5 rounded-2xl shadow-2xl flex items-center gap-4 pointer-events-auto animate-bounce ${
              isDark ? 'bg-gradient-to-r from-indigo-600 to-purple-600 shadow-indigo-500/30' : 'bg-gradient-to-r from-indigo-500 to-purple-500 shadow-indigo-400/30'
            }`}>
              <span className="text-3xl">👋</span>
              <div>
                <p className="font-bold text-xl text-white">Welcome back, {welcomeToast}!</p>
                <p className="text-sm text-white/70">Great to see you again</p>
              </div>
            </div>
          </div>
        )}

        {/* ─── Points Earned Popup ─── */}
        {pointsToast && (
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-testid="points-toast">
            <div className="bg-gradient-to-r from-amber-500 to-yellow-400 text-black px-8 py-5 rounded-2xl shadow-2xl shadow-amber-500/30 flex items-center gap-4 animate-bounce pointer-events-auto">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Trophy className="w-6 h-6" />
              </div>
              <div>
                <p className="font-bold text-xl">+{pointsToast.points} Points!</p>
                <p className="text-sm opacity-80">Total: {pointsToast.total} pts</p>
              </div>
            </div>
          </div>
        )}

        {/* ─── Tong Shu Day Officer Calendar ─── */}
        {tongShu && (
          <a href="https://onlinetongshu.com/calendar" target="_blank" rel="noopener noreferrer"
            className="block mb-6 group" data-testid="tongshu-card">
            <div className={`rounded-xl overflow-hidden border transition-all group-hover:border-amber-500/40 ${
              isDark ? 'bg-gradient-to-r from-[#141414] to-[#1a1a1a] border-[#252525]' : 'bg-gradient-to-r from-white to-gray-50 border-gray-200 shadow-sm'
            }`}>
              <div className="flex items-stretch">
                {/* Day Pillar */}
                <div className={`px-5 py-4 flex flex-col items-center justify-center border-r shrink-0 ${
                  isDark ? 'border-[#252525] bg-red-900/20' : 'border-gray-200 bg-red-50'
                }`}>
                  <p className="text-3xl font-bold text-red-500 leading-none">{tongShu.day_pillar.full}</p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{tongShu.day_pillar.animal}</p>
                </div>

                {/* Day Officer */}
                <div className="px-5 py-4 flex flex-col items-center justify-center shrink-0">
                  <p className={`text-2xl font-bold ${tongShu.auspicious ? 'text-emerald-400' : 'text-red-400'}`}>
                    {tongShu.officer.chinese}
                  </p>
                  <p className="text-xs text-gray-500">{tongShu.officer.english}</p>
                </div>

                {/* Auspicious Badge */}
                <div className="px-4 py-4 flex items-center shrink-0">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    tongShu.auspicious
                      ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                      : 'bg-red-500/15 text-red-400 border border-red-500/30'
                  }`}>
                    {tongShu.auspicious_text}
                  </span>
                </div>

                {/* Directions + Fortune */}
                <div className="px-4 py-4 flex flex-col justify-center flex-1 min-w-0">
                  <div className="flex items-center gap-4 mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-pink-400 text-xs">喜</span>
                      <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{tongShu.directions.happiness}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-amber-400 text-xs">财</span>
                      <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{tongShu.directions.wealth}</span>
                    </div>
                  </div>
                  {tongShu.fortune && (
                    <p className={`text-xs italic line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {tongShu.fortune}
                    </p>
                  )}
                </div>

                {/* Date + CTA */}
                <div className="px-5 py-4 flex flex-col items-end justify-center shrink-0">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{tongShu.display_date}</p>
                  <p className="text-xs text-amber-400 group-hover:text-amber-300 mt-1 flex items-center gap-1">
                    View Full Calendar <ExternalLink className="w-3 h-3" />
                  </p>
                </div>
              </div>
            </div>
          </a>
        )}

        {/* ─── Welcome Header ─── */}
        <div className="mb-8" data-testid="portal-header">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className={`${t.amber} text-sm uppercase tracking-widest mb-1`}>Student Portal</p>
              <h1 className={`text-3xl md:text-4xl font-light ${t.text}`}>
                {login.count > 1 ? 'Welcome back, ' : 'Welcome, '}<span className="font-serif italic !text-orange-500">{user.name}</span>
              </h1>
            </div>
            <div className="flex items-center gap-3 self-start">
              <button onClick={toggleTheme}
                className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-[#1a1a1a] text-amber-400 hover:bg-[#252525]' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                data-testid="theme-toggle">
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <Button onClick={() => navigate('/courses')} className="bg-amber-600 hover:bg-amber-500" data-testid="explore-courses-btn">
                <BookOpen className="w-4 h-4 mr-2" /> Explore All Courses
              </Button>
              <a
                href="https://wa.me/601171383699"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-contact-btn"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-medium rounded-md transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Contact Us
              </a>
            </div>
          </div>
        </div>

        {/* ─── Stats Row ─── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-row">
          <Card className={`${t.card} portal-card`}>
            <CardContent className="p-5 text-center">
              <Trophy className="w-6 h-6 mx-auto mb-2" style={{ color: points.rank.color }} />
              <p className="text-2xl font-bold portal-text">{points.total}</p>
              <p className="text-xs portal-text-muted uppercase tracking-wider">Total Points</p>
            </CardContent>
          </Card>
          <Card className={`${t.card} portal-card`}>
            <CardContent className="p-5 text-center">
              <Award className="w-6 h-6 mx-auto mb-2" style={{ color: points.rank.color }} />
              <p className="text-2xl font-bold" style={{ color: points.rank.color }}>{points.rank.name}</p>
              <p className="text-xs portal-text-muted uppercase tracking-wider">{points.rank.chinese}</p>
            </CardContent>
          </Card>
          <Card className={`${t.card} portal-card`}>
            <CardContent className="p-5 text-center">
              <Flame className="w-6 h-6 mx-auto mb-2 text-orange-400" />
              <p className="text-2xl font-bold portal-text">{login.streak}</p>
              <p className="text-xs portal-text-muted uppercase tracking-wider">Day Streak</p>
            </CardContent>
          </Card>
          <Card className={`${t.card} portal-card`}>
            <CardContent className="p-5 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-blue-400" />
              <p className="text-2xl font-bold portal-text">{login.count}</p>
              <p className="text-xs portal-text-muted uppercase tracking-wider">Total Logins</p>
            </CardContent>
          </Card>
        </div>

        {/* ─── Your Daily Practice ─── */}
        {sectionVisible('daily_practice') && dailyPractice && (
          <div className="mb-8" data-testid="daily-practice">
            <div className="mb-4">
              <p className="text-sm text-gray-500 mb-1">
                <Calendar className="w-3.5 h-3.5 inline mr-1" />
                {new Date().toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
              <h2 className="text-xl font-semibold portal-text">Your Daily Practice</h2>
            </div>

            {/* Affirmation + Mood Check-in (same row) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Card className={`${t.card} portal-card overflow-hidden`}>
                <div className={`p-5 h-full ${isDark ? 'bg-gradient-to-br from-purple-900/40 to-indigo-900/30' : 'bg-gradient-to-br from-purple-100 to-indigo-100'}`}>
                  <p className="text-purple-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4" /> Today's Affirmation
                  </p>
                  <p className={`text-2xl italic leading-relaxed ${isDark ? '!text-gray-200' : '!text-gray-800'}`}>
                    "{dailyPractice.affirmation}"
                  </p>
                </div>
              </Card>

              <Card className={`${t.card} portal-card`}>
                <CardContent className="p-5">
                  <p className="text-pink-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Heart className="w-4 h-4" /> How are you feeling today?
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { key: 'great', emoji: '\u{1F60A}', label: 'Great' },
                      { key: 'good', emoji: '\u{1F642}', label: 'Good' },
                      { key: 'okay', emoji: '\u{1F610}', label: 'Okay' },
                      { key: 'low', emoji: '\u{1F614}', label: 'Low' },
                      { key: 'struggling', emoji: '\u{1F622}', label: 'Struggling' },
                    ].map(m => {
                      const selected = dailyPractice.checkin?.mood === m.key;
                      return (
                        <button key={m.key} onClick={() => saveCheckin('mood', m.key)}
                          disabled={savingCheckin}
                          className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all ${
                            selected
                              ? 'bg-pink-500/20 border border-pink-500/40 scale-105'
                              : `${isDark ? 'bg-[#1a1a1a] hover:bg-[#252525]' : 'bg-gray-100 hover:bg-gray-200'} border border-transparent`
                          }`}
                          data-testid={`mood-${m.key}`}>
                          <span className="text-xl">{m.emoji}</span>
                          <span className={`text-[10px] ${selected ? 'text-pink-400' : 'text-gray-500'}`}>{m.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-gray-600 mt-3 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {dailyPractice.checkin_count_year} check-ins this year
                  </p>

                  {/* Today's Summary */}
                  {dailyPractice.checkin && (dailyPractice.checkin.mood || dailyPractice.checkin.goal || dailyPractice.checkin.commitment) && (
                    <div className={`mt-4 pt-3 border-t ${isDark ? 'border-[#252525]' : 'border-gray-200'}`}>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Today's Picks</p>
                      <div className="space-y-1.5">
                        {dailyPractice.checkin.mood && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{
                              { great: '\u{1F60A}', good: '\u{1F642}', okay: '\u{1F610}', low: '\u{1F614}', struggling: '\u{1F622}' }[dailyPractice.checkin.mood]
                            }</span>
                            <span className="text-xs text-gray-400 capitalize">{dailyPractice.checkin.mood}</span>
                          </div>
                        )}
                        {dailyPractice.checkin.goal && (
                          <div className="flex items-center gap-2">
                            <Target className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                            <span className="text-xs text-gray-400 truncate">{dailyPractice.checkin.goal}</span>
                          </div>
                        )}
                        {dailyPractice.checkin.commitment && (
                          <div className="flex items-center gap-2">
                            <Zap className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                            <span className="text-xs text-gray-400 truncate">{dailyPractice.checkin.commitment}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Goal & Commitment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className={`${t.card} portal-card`}>
                <CardContent className="p-5">
                  <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Target className="w-4 h-4" /> My Goal Today
                  </p>
                  <select
                    value={dailyPractice.checkin?.goal || ''}
                    onChange={e => saveCheckin('goal', e.target.value)}
                    disabled={savingCheckin}
                    className={`w-full px-3 py-2.5 rounded-lg text-sm border ${
                      isDark ? 'bg-[#1a1a1a] border-gray-700 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'
                    }`}
                    data-testid="goal-select">
                    <option value="">Select your goal...</option>
                    {dailyPractice.goals.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                  {dailyPractice.checkin?.goal && (
                    <p className="mt-3 text-sm text-cyan-400 flex items-center gap-1.5">
                      <Target className="w-3 h-3" /> {dailyPractice.checkin.goal}
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card className={`${t.card} portal-card`}>
                <CardContent className="p-5">
                  <p className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Zap className="w-4 h-4" /> My Commitment
                  </p>
                  <select
                    value={dailyPractice.checkin?.commitment || ''}
                    onChange={e => saveCheckin('commitment', e.target.value)}
                    disabled={savingCheckin}
                    className={`w-full px-3 py-2.5 rounded-lg text-sm border ${
                      isDark ? 'bg-[#1a1a1a] border-gray-700 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'
                    }`}
                    data-testid="commitment-select">
                    <option value="">Choose your action...</option>
                    {dailyPractice.commitments.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {dailyPractice.checkin?.commitment && (
                    <p className="mt-3 text-sm text-amber-400 flex items-center gap-1.5">
                      <Zap className="w-3 h-3" /> {dailyPractice.checkin.commitment}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ─── Membership ─── */}
        {sectionVisible('membership') && user.membership_start && (
          <div className="mb-8" data-testid="membership-card">
            <Card className="bg-[#141414] border-[#252525] overflow-hidden">
              <CardContent className="p-0">
                <div className="flex flex-col sm:flex-row">
                  <div className={`p-6 sm:w-64 shrink-0 flex flex-col items-center justify-center border-b sm:border-b-0 sm:border-r ${isDark ? 'bg-gradient-to-br from-sky-600/20 to-indigo-600/20 border-[#252525]' : 'bg-gradient-to-br from-sky-800 to-indigo-900 border-gray-200'}`}>
                    <Award className="w-8 h-8 text-white mb-2" />
                    <p className="text-lg font-semibold text-center !text-white">
                      {user.membership_duration === '2_year' ? '2 Year' : user.membership_duration === 'extended' ? 'Extended' : '1 Year'}
                    </p>
                    <p className="text-sm font-medium text-center !text-sky-200">Life Mastermind Circle</p>
                  </div>
                  <div className="p-6 flex-1">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Member Since</p>
                        <p className="text-sm font-medium text-white">
                          {new Date(user.membership_start + 'T00:00:00').toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Valid Until</p>
                        <p className={`text-sm font-medium ${new Date(user.membership_end + 'T00:00:00') < new Date() ? 'text-red-400' : 'text-emerald-400'}`}>
                          {new Date(user.membership_end + 'T00:00:00').toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Days Left</p>
                        {(() => {
                          const daysLeft = Math.max(0, Math.ceil((new Date(user.membership_end + 'T00:00:00') - new Date()) / (1000 * 60 * 60 * 24)));
                          const totalDays = Math.ceil((new Date(user.membership_end + 'T00:00:00') - new Date(user.membership_start + 'T00:00:00')) / (1000 * 60 * 60 * 24));
                          return (
                            <>
                              <p className={`text-sm font-medium ${daysLeft > 30 ? 'text-white' : daysLeft > 0 ? 'text-amber-400' : 'text-red-400'}`}>
                                {daysLeft > 0 ? daysLeft : 'Expired'}
                              </p>
                              <Progress value={totalDays > 0 ? ((totalDays - daysLeft) / totalDays) * 100 : 100} className="h-1 mt-2 bg-[#252525]" />
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#252525]">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2.5">Your Entitlements</p>
                      {user.entitlements && user.entitlements.filter(e => e.enabled !== false).length > 0 ? (
                        <div className="space-y-2.5">
                          {user.entitlements.filter(e => e.enabled !== false).map((item) => {
                            const remaining = item.total - item.used;
                            return (
                              <div key={item.key} className="flex items-center justify-between gap-3">
                                <p className={`text-sm ${remaining > 0 ? 'text-gray-300' : 'text-gray-600 line-through'}`}>
                                  {item.total} x {item.label}
                                </p>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
                                  remaining > 0
                                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                    : 'bg-gray-800 text-gray-500 border border-gray-700'
                                }`}>
                                  {remaining > 0 ? `${remaining} left` : 'Used'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-600">No entitlements set</p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Rank Progress + Login Activity ─── */}
        {sectionVisible('rank_login') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Rank Progress */}
          <Card className={`${t.card} portal-card`} data-testid="rank-progress">
            <CardContent className="p-6">
              <h3 className="portal-text font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-amber-400" /> Rank Progress
              </h3>
              <div className="mb-4">
                <p className="portal-text font-medium" style={{ color: points.rank.color }}>{points.rank.name}</p>
                {points.next_rank ? (
                  <p className="text-xs text-gray-500">
                    {points.next_rank.min_points - points.total} pts to {points.next_rank.name}
                  </p>
                ) : (
                  <p className="text-xs text-amber-400">Maximum rank achieved!</p>
                )}
              </div>
              <Progress value={progressToNext} className="h-2 bg-[#252525]" />
              <div className="flex justify-between mt-3">
                {ranks.map(r => (
                  <div key={r.name} className="text-center group relative">
                    <div className={`w-3 h-3 rounded-full mx-auto ${points.total >= r.min_points ? '' : 'opacity-30'}`}
                      style={{ background: r.color }} />
                    <p className="text-[9px] mt-1 hidden md:block" style={{ color: points.total >= r.min_points ? r.color : '#555' }}>
                      {r.name}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Login Activity - Monthly Calendar */}
          <Card className={`${t.card} portal-card`} data-testid="login-activity">
            <CardContent className="p-4">
              <h3 className="portal-text text-sm font-semibold mb-3 flex items-center gap-2">
                <Flame className="w-3.5 h-3.5 text-orange-400" /> Login Activity — {login.month || 'This Month'}
              </h3>
              <div className="grid grid-cols-7 gap-0.5 mb-0.5">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                  <div key={i} className="text-center text-[9px] text-gray-600 uppercase py-0.5">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {Array.from({ length: login.first_weekday || 0 }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {login.activity.map((day) => {
                  const dayNum = new Date(day.date + 'T00:00:00').getDate();
                  const isToday = day.date === new Date().toISOString().split('T')[0];
                  return (
                    <div key={day.date}
                      className={`h-7 rounded flex items-center justify-center ${
                        day.logged_in
                          ? 'bg-emerald-500/20 border border-emerald-500/40'
                          : isToday
                            ? 'bg-amber-500/10 border border-amber-500/30'
                            : `${t.calEmpty}`
                      }`}>
                      <span className={`text-[10px] font-medium ${
                        day.logged_in ? 'text-emerald-400' : isToday ? 'text-amber-400' : 'text-gray-600'
                      }`}>{dayNum}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#252525] text-[10px]">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500/30 border border-emerald-500/40" /> Logged in</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500/20 border border-amber-500/30" /> Today</span>
                </div>
                <p className="text-gray-500">
                  {login.activity.filter(d => d.logged_in).length}/{login.activity.length} days
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* ─── My Journey + My Progress (side by side) ─── */}
        {sectionVisible('journey') && journey && (
          <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="journey-section">
            {/* My Journey */}
            <div>
              <h2 className="text-xl font-semibold portal-text mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-400" /> My Journey
              </h2>

              {/* Stats Row */}
              <div className="grid grid-cols-5 gap-1.5 mb-4">
                {[
                  { label: 'Activities', val: journey.stats.total_activities, color: 'text-white' },
                  { label: 'Logins', val: journey.stats.logins, color: 'text-blue-400' },
                  { label: 'Check-ins', val: journey.stats.mood_checkins, color: 'text-pink-400' },
                  { label: 'Goals', val: journey.stats.goals_set, color: 'text-cyan-400' },
                  { label: 'Explored', val: journey.stats.ecosystem_visits, color: 'text-purple-400' },
                ].map(s => (
                  <div key={s.label} className={`portal-card-inner rounded-lg p-2 text-center ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>
                    <p className={`text-lg font-bold ${s.color}`}>{s.val}</p>
                    <p className="text-[9px] text-gray-500 uppercase">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Timeline */}
              {journey.activities.length > 0 ? (
              <Card className={`${t.card} portal-card`}>
                <CardContent className="p-4 max-h-[400px] overflow-y-auto">
                  <div className="space-y-0">
                    {journey.activities.slice(0, 15).map((a, i) => {
                      const icons = {
                        login: { icon: '\u{1F511}', color: 'border-blue-500/40 bg-blue-500/10' },
                        streak: { icon: '\u{1F525}', color: 'border-orange-500/40 bg-orange-500/10' },
                        mood_checkin: { icon: '\u{1F497}', color: 'border-pink-500/40 bg-pink-500/10' },
                        goal_set: { icon: '\u{1F3AF}', color: 'border-cyan-500/40 bg-cyan-500/10' },
                        commitment_set: { icon: '\u{26A1}', color: 'border-amber-500/40 bg-amber-500/10' },
                        ecosystem_visit: { icon: '\u{1F310}', color: 'border-purple-500/40 bg-purple-500/10' },
                        merch_redeem: { icon: '\u{1F6CD}', color: 'border-emerald-500/40 bg-emerald-500/10' },
                        course_enrollment: { icon: '\u{1F4DA}', color: 'border-blue-500/40 bg-blue-500/10' },
                        lesson_completed: { icon: '\u{2705}', color: 'border-green-500/40 bg-green-500/10' },
                        course_completed: { icon: '\u{1F3C6}', color: 'border-amber-500/40 bg-amber-500/10' },
                      };
                      const style = icons[a.action] || { icon: '\u{1F4CC}', color: 'border-gray-500/40 bg-gray-500/10' };
                      const timeAgo = (() => {
                        const diff = Date.now() - new Date(a.created_at).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) return 'just now';
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        const days = Math.floor(hrs / 24);
                        return `${days}d ago`;
                      })();
                      return (
                        <div key={i} className="flex gap-3 items-start">
                          <div className="flex flex-col items-center">
                            <div className={`w-7 h-7 rounded-full border flex items-center justify-center text-xs ${style.color}`}>
                              {style.icon}
                            </div>
                            {i < Math.min(journey.activities.length, 15) - 1 && (
                              <div className={`w-px h-5 ${isDark ? 'bg-[#252525]' : 'bg-gray-200'}`} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 pb-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm portal-text truncate">{a.description}</p>
                              {a.points !== 0 && (
                                <span className={`text-xs font-semibold shrink-0 ${a.points > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {a.points > 0 ? '+' : ''}{a.points} pts
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-gray-600">{timeAgo}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              ) : (
                <Card className={`${t.card} portal-card`}>
                  <CardContent className="p-6 text-center">
                    <Activity className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Start your journey!</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* My Progress */}
            <div data-testid="points-log">
              <h2 className="text-xl font-semibold portal-text mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" /> My Progress
              </h2>
              <Card className={`${t.card} portal-card mb-4`}>
                <CardContent className="p-4 max-h-[280px] overflow-y-auto">
                  {points_log.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">Start earning points by logging in daily!</p>
                  ) : (
                    <div className="space-y-3">
                      {points_log.map((entry, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0">
                          <div>
                            <p className="text-sm text-gray-300">{entry.description}</p>
                            <p className="text-[10px] text-gray-600">
                              {new Date(entry.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                            </p>
                          </div>
                          <span className="text-sm font-bold text-emerald-400">+{entry.points}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* How to Earn */}
              <Card className={`${t.card} portal-card`}>
                <CardContent className="p-4">
                  <p className="text-xs uppercase tracking-widest text-gray-600 mb-3">How to Earn Points</p>
                  <div className="space-y-2">
                    {[
                      { action: 'Daily Login', pts: 10 },
                      { action: 'Login Streak Bonus', pts: 10 },
                      { action: 'My Goal Today', pts: 5 },
                      { action: 'How are you feeling today?', pts: 5 },
                      { action: 'My Commitment', pts: 5 },
                      { action: 'Course Enrollment', pts: 20 },
                      { action: 'Lesson Completed', pts: 10 },
                      { action: 'Course Completed', pts: 100 },
                    ].map(r => (
                      <div key={r.action} className="flex justify-between items-center">
                        <span className="text-xs text-gray-400">{r.action}</span>
                        <span className="text-xs font-medium text-amber-400">+{r.pts}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ─── Schedule & Activities ─── */}
        {sectionVisible('schedule') && (
        <div className="mb-8" data-testid="schedule-section">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-xl font-semibold portal-text flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400" /> Schedule & Activities
            </h2>
            <div className="flex items-center gap-2">
              <div className={`flex ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-200'} rounded-lg p-0.5`}>
                {['monthly', 'yearly'].map(v => (
                  <button key={v} onClick={() => setScheduleView(v)}
                    className={`px-3 py-1 text-xs rounded-md transition-colors capitalize ${
                      scheduleView === v ? 'bg-amber-600 text-white' : `${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`
                    }`} data-testid={`schedule-tab-${v}`}>
                    {v}
                  </button>
                ))}
              </div>
              {scheduleView === 'monthly' && (
                <div className="flex items-center gap-1">
                  <button onClick={() => {
                    const [y, m] = selectedMonth.split('-').map(Number);
                    const d = new Date(y, m - 2, 1);
                    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
                  }} className="p-1 text-gray-500 hover:text-white" data-testid="prev-month">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-300 min-w-[100px] text-center">
                    {new Date(selectedMonth + '-01T00:00:00').toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                  </span>
                  <button onClick={() => {
                    const [y, m] = selectedMonth.split('-').map(Number);
                    const d = new Date(y, m, 1);
                    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
                  }} className="p-1 text-gray-500 hover:text-white" data-testid="next-month">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              {scheduleView === 'yearly' && (
                <div className="flex items-center gap-1">
                  <button onClick={() => {
                    const [y] = selectedMonth.split('-').map(Number);
                    setSelectedMonth(`${y - 1}-01`);
                  }} className="p-1 text-gray-500 hover:text-white" data-testid="prev-year">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-300 min-w-[50px] text-center">
                    {selectedMonth.split('-')[0]}
                  </span>
                  <button onClick={() => {
                    const [y] = selectedMonth.split('-').map(Number);
                    setSelectedMonth(`${y + 1}-01`);
                  }} className="p-1 text-gray-500 hover:text-white" data-testid="next-year">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {scheduleItems.length === 0 ? (
            <Card className={`${t.card} portal-card`}>
              <CardContent className="p-8 text-center">
                <Calendar className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  {scheduleView === 'monthly' ? 'No events scheduled this month' : 'No activities this year'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {scheduleItems.map(item => {
                const catColors = {
                  class: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
                  workshop: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
                  event: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
                  webinar: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
                  milestone: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
                  theme: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
                };
                const catLabels = { class: 'Class', workshop: 'Workshop', event: 'Event', webinar: 'Webinar', milestone: 'Milestone', theme: 'Theme' };
                const style = catColors[item.category] || catColors.event;
                return (
                  <Card key={item.item_id}
                    className="bg-[#141414] border-[#252525] hover:border-amber-500/20 transition-colors cursor-pointer"
                    onClick={() => setExpandedSchedule(expandedSchedule === item.item_id ? null : item.item_id)}
                    data-testid={`schedule-card-${item.item_id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <div className="w-12 h-12 rounded-lg portal-card-inner flex flex-col items-center justify-center shrink-0">
                          <span className="text-[10px] text-gray-500 uppercase leading-none">
                            {item.date ? new Date(item.date + 'T00:00:00').toLocaleDateString('en', { month: 'short' }) : ''}
                          </span>
                          <span className="text-lg font-bold portal-text leading-none">
                            {item.date ? new Date(item.date + 'T00:00:00').getDate() : ''}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                              <p className="portal-text font-medium text-sm truncate">{item.title}</p>
                            <span className={`text-[10px] px-1.5 py-0 rounded border ${style}`}>
                              {catLabels[item.category] || 'Event'}
                            </span>
                          </div>
                          {item.description && expandedSchedule !== item.item_id && (
                            <p className="text-xs text-gray-500 line-clamp-1">{item.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-600">
                            {item.time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.time}</span>}
                            {item.location_link && (
                              item.location_link.startsWith('http') ? (
                                <a href={item.location_link} target="_blank" rel="noopener noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  className="flex items-center gap-1 text-amber-500 hover:text-amber-400">
                                  <MapPin className="w-3 h-3" />Join
                                </a>
                              ) : (
                                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location_link}</span>
                              )
                            )}
                          </div>
                        </div>
                        <ChevronRight className={`w-4 h-4 text-gray-600 shrink-0 mt-1 transition-transform ${expandedSchedule === item.item_id ? 'rotate-90' : ''}`} />
                      </div>
                      {expandedSchedule === item.item_id && item.description && (
                        <div className="mt-3 pt-3 border-t border-[#252525]">
                          <p className="text-sm text-gray-400 whitespace-pre-wrap">{item.description}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* ─── Qimen Activation Date & Time ─── */}
        {sectionVisible('qimen') && qimenItems.length > 0 && (
          <div className="mb-8" data-testid="qimen-section">
            <h2 className="text-xl font-semibold portal-text mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-400" /> Qimen Activation Date & Time
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {qimenItems.map(item => (
                <Card key={item.item_id} className={`${t.card} portal-card hover:border-purple-500/30 transition-colors`}
                  data-testid={`qimen-card-${item.item_id}`}>
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-purple-500/15 border border-purple-500/30 flex flex-col items-center justify-center shrink-0">
                      <span className="text-[10px] text-purple-400 uppercase leading-none">
                        {item.date ? new Date(item.date + 'T00:00:00').toLocaleDateString('en', { month: 'short' }) : ''}
                      </span>
                      <span className="text-lg font-bold text-purple-300 leading-none">
                        {item.date ? new Date(item.date + 'T00:00:00').getDate() : ''}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="portal-text font-medium text-sm">{item.label}</p>
                      <p className="text-xs text-gray-500">
                        {item.date ? new Date(item.date + 'T00:00:00').toLocaleDateString('en', { weekday: 'short', day: 'numeric', month: 'short' }) : ''}
                        {item.time && <span className="text-purple-400"> — {item.time}</span>}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ─── Merchandise ─── */}
        {sectionVisible('merchandise') && merchItems.filter(i => i.featured).length > 0 && (
          <div className="mb-8" data-testid="merch-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold portal-text flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-amber-400" /> Merchandise
              </h2>
              <button onClick={() => navigate('/merchandise')}
                className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                data-testid="view-all-merch">
                View All &rarr;
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {merchItems.filter(i => i.featured).map(item => (
                <Card key={item.item_id} className={`${t.card} portal-card overflow-hidden hover:border-amber-500/30 transition-colors`}
                  data-testid={`merch-card-${item.item_id}`}>
                  {item.image_url && (
                    <div className="w-full h-40 overflow-hidden">
                      <img src={item.image_url.startsWith('/api') ? `${BACKEND_URL}${item.image_url}` : item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="portal-text font-medium text-sm">{item.name}</p>
                      <span className="text-amber-400 font-semibold text-sm shrink-0">{item.price}</span>
                    </div>
                    {item.description && <p className="text-xs text-gray-500 mb-3">{item.description}</p>}
                    <a
                      href={`https://wa.me/601171383699?text=${encodeURIComponent(`Hi, I'd like to order: ${item.name} (${item.price})`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 w-full justify-center px-3 py-2 bg-[#25D366] hover:bg-[#1ebe57] text-white text-sm font-medium rounded-md transition-colors"
                      data-testid={`order-${item.item_id}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Order via WhatsApp
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ─── Ecosystem Hub ─── */}
        {sectionVisible('ecosystem') && (
        <div className="mb-8" data-testid="ecosystem-hub">
          <h2 className="text-xl font-semibold portal-text mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" /> Life Decision System
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {ecosystem.map((site) => {
              const Icon = ICON_MAP[site.icon] || Compass;
              const isComingSoon = site.coming_soon;
              return (
                <Card
                  key={site.key}
                  className={`border transition-all ${isComingSoon
                    ? `${isDark ? 'bg-[#141414] border-[#252525]' : 'bg-white border-gray-200'} opacity-60`
                    : 'bg-[#141414] border-[#252525] cursor-pointer group hover:border-amber-500/40 hover:-translate-y-0.5'}`}
                  onClick={() => !isComingSoon && trackVisit(site.key, site.url)}
                  data-testid={`ecosystem-${site.key}`}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                      isComingSoon ? 'bg-gray-500/10' : 'bg-amber-500/10 group-hover:bg-amber-500/20'
                    }`}>
                      <Icon className={`w-5 h-5 ${isComingSoon ? 'text-gray-500' : 'text-amber-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                          <p className="portal-text font-medium text-sm truncate">{site.name}</p>
                        {isComingSoon ? (
                          <span className="text-[10px] px-1.5 py-0 rounded bg-gray-500/20 text-gray-400 border border-gray-500/30">Soon</span>
                        ) : (
                          <ExternalLink className="w-3 h-3 text-gray-600 shrink-0 group-hover:text-amber-400 transition-colors" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{site.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
        )}

        {/* ─── Lifetunes ─── */}
        {sectionVisible('training') && (
        <div className="mb-8" data-testid="lifetunes-section">
          <h2 className="text-xl font-semibold portal-text mb-4 flex items-center gap-2">
            <Music className="w-5 h-5 text-teal-400" /> Lifetunes
          </h2>
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Wellness frequencies to align your energy, focus, and flow.
          </p>

          {lifetuneTracks.length > 0 ? (
            <>
              {/* Featured Track */}
              {lifetuneTracks.filter(t => t.featured).map((track, i) => (
                <div key={track.track_id || i}
                  className={`rounded-xl overflow-hidden mb-4 ${isDark ? 'bg-gradient-to-r from-teal-900/30 via-[#111] to-cyan-900/30 border border-teal-500/20' : 'bg-gradient-to-r from-teal-50 via-white to-cyan-50 border border-teal-200'}`}
                  data-testid="lifetunes-featured">
                  <div className="flex items-center gap-4 p-5">
                    <button onClick={() => window.open(track.link_url, '_blank')}
                      className="shrink-0 w-16 h-16 rounded-full overflow-hidden border border-teal-500/40 flex items-center justify-center hover:scale-105 transition-all cursor-pointer"
                      data-testid="lifetunes-featured-play">
                      {track.image_url ? (
                        <img src={track.image_url} alt={track.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-teal-500/20 flex items-center justify-center">
                          <Play className="w-7 h-7 text-teal-400 ml-0.5" />
                        </div>
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>{track.title}</p>
                      {track.subtitle && <p className={`text-xs mt-0.5 ${isDark ? 'text-teal-400/70' : 'text-teal-600'}`}>{track.subtitle}</p>}
                      <div className="mt-2 flex items-center gap-2">
                        <div className={`h-1 flex-1 rounded-full overflow-hidden ${isDark ? 'bg-teal-900/40' : 'bg-teal-100'}`}>
                          <div className="h-full w-0 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full animate-pulse" />
                        </div>
                      </div>
                    </div>
                    <button onClick={() => window.open(track.link_url, '_blank')}
                      className="shrink-0 px-4 py-2 rounded-lg bg-teal-500/10 border border-teal-500/30 text-teal-400 text-xs font-medium hover:bg-teal-500/20 transition-colors">
                      Open
                    </button>
                  </div>
                </div>
              ))}

              {/* Track List */}
              <div className="space-y-2">
                {lifetuneTracks.filter(t => !t.featured).map((track, i) => (
                  <button key={track.track_id || i} onClick={() => window.open(track.link_url, '_blank')}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all hover:scale-[1.01] cursor-pointer ${isDark ? 'bg-[#141414] border border-gray-800/60 hover:border-teal-500/30' : 'bg-white border border-gray-200 hover:border-teal-300'}`}
                    data-testid={`lifetunes-track-${i}`}>
                    <div className="shrink-0 w-10 h-10 rounded-lg overflow-hidden">
                      {track.image_url ? (
                        <img src={track.image_url} alt={track.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-teal-500/10' : 'bg-teal-50'}`}>
                          <Play className="w-4 h-4 text-teal-400 ml-0.5" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{track.title}</p>
                      {track.subtitle && <p className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{track.subtitle}</p>}
                    </div>
                    <ExternalLink className={`w-3.5 h-3.5 shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className={`text-center py-8 rounded-xl ${isDark ? 'bg-[#111] border border-gray-800/60' : 'bg-gray-50 border border-gray-200'}`}>
              <Music className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Tracks coming soon</p>
            </div>
          )}
        </div>
        )}

        {/* ─── Training Videos ─── */}
        {sectionVisible('training') && trainingData && trainingData.videos.length > 0 && (
          <div className="mb-8" data-testid="training-section">
            <h2 className="text-xl font-semibold portal-text mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-purple-400" /> Special Training
            </h2>

            {/* Category Tabs */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {trainingData.categories.map(cat => {
                const count = trainingData.videos.filter(v => v.category === cat.key).length;
                if (count === 0) return null;
                return (
                  <button key={cat.key} onClick={() => setActiveTrainingCat(cat.key)}
                    className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                      activeTrainingCat === cat.key
                        ? 'bg-purple-600 text-white'
                        : `${isDark ? 'bg-[#1a1a1a] text-gray-400 hover:text-white' : 'bg-gray-200 text-gray-600 hover:text-gray-800'}`
                    }`}
                    data-testid={`training-tab-${cat.key}`}>
                    {cat.label} ({count})
                  </button>
                );
              })}
            </div>

            {/* Videos Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trainingData.videos.filter(v => v.category === activeTrainingCat).map(v => {
                const videoId = v.video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([^&?\s]+)/)?.[1];
                return (
                  <Card key={v.video_id} className={`${t.card} portal-card overflow-hidden`}
                    data-testid={`training-card-${v.video_id}`}>
                    {videoId ? (
                      <div className="aspect-video">
                        <iframe src={`https://www.youtube.com/embed/${videoId}`}
                          className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen title={v.title} />
                      </div>
                    ) : (
                      <a href={v.video_url} target="_blank" rel="noopener noreferrer"
                        className={`aspect-video flex items-center justify-center ${isDark ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>
                        <Play className="w-10 h-10 text-purple-400" />
                      </a>
                    )}
                    <CardContent className="p-4">
                      <p className="portal-text font-medium text-sm">{v.title}</p>
                      {v.description && <p className="text-xs text-gray-500 mt-1">{v.description}</p>}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── My Courses ─── */}
        {sectionVisible('courses') && (
        <div className="mb-8" data-testid="my-courses">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold portal-text flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-amber-400" /> My Courses
              </h2>
              <span className="text-xs text-gray-500">{courses.enrolled.length} of {courses.total_available} enrolled</span>
            </div>
            {courses.enrolled.length === 0 ? (
              <Card className={`${t.card} portal-card`}>
                <CardContent className="p-10 text-center">
                  <BookOpen className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 mb-4">You haven't enrolled in any courses yet.</p>
                  <Button onClick={() => navigate('/courses')} className="bg-amber-600 hover:bg-amber-500">
                    Browse Courses <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {courses.enrolled.map(course => (
                  <Card key={course.id} className="bg-[#141414] border-[#252525] cursor-pointer hover:border-amber-500/30 transition-all"
                    onClick={() => navigate(`/course/${course.id}`)} data-testid={`course-${course.id}`}>
                    <CardContent className="p-4 flex gap-4">
                      <div className="w-24 h-16 rounded-lg overflow-hidden shrink-0 portal-card-inner">
                        {course.thumbnail && (
                          <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="portal-text font-medium text-sm truncate">{course.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{course.level} · {course.completed_lessons}/{course.total_lessons} lessons</p>
                        <div className="flex items-center gap-3 mt-2">
                          <Progress value={course.progress_percent} className="h-1.5 flex-1 bg-[#252525]" />
                          <span className="text-xs font-medium text-amber-400">{course.progress_percent}%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentPortal;
